import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Trash, Plus, PencilSimple, X } from "@phosphor-icons/react";
import ImageUploader from "../components/ImageUploader";

const EMPTY = { name: "", slug: "", description: "", price: "", category: "bras", images: [], sizes: ["XS", "S", "M", "L", "XL"], colors: ["Noir"], stock: 100, featured: false };

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null); // null = add mode; product.id = edit mode
  const [tab, setTab] = useState("products");

  const existingCategories = Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort();

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!user.is_admin) { nav("/"); return; }
    load();
  }, [user, loading, nav]);

  const load = async () => {
    const [p, o] = await Promise.all([api.get("/products"), api.get("/admin/orders")]);
    setProducts(p.data);
    setOrders(o.data);
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      slug: p.slug,
      description: p.description,
      price: p.price,
      category: p.category,
      images: (p.images || []).filter(Boolean),
      sizes: p.sizes || ["XS", "S", "M", "L", "XL"],
      colors: p.colors || [],
      stock: p.stock,
      featured: !!p.featured,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        category: (form.category || "").toLowerCase().trim().replace(/\s+/g, "-"),
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
        images: form.images.filter(Boolean),
        colors: typeof form.colors === "string" ? form.colors.split(",").map(s => s.trim()).filter(Boolean) : form.colors,
        sizes: typeof form.sizes === "string" ? form.sizes.split(",").map(s => s.trim()).filter(Boolean) : form.sizes,
      };
      if (!payload.category) { toast.error("Category is required"); return; }
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      cancelEdit();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Removed");
    if (editingId === id) cancelEdit();
    load();
  };

  const markPaid = async (orderId) => {
    if (!confirm("Mark this COD order as paid & delivered?")) return;
    try {
      await api.post(`/admin/orders/${orderId}/mark-paid`);
      toast.success("Order marked delivered");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-12 bg-white min-h-screen" data-testid="admin-page">
      <div className="overline text-[#4A1525] mb-2">Iamravishing</div>
      <h1 className="font-serif text-5xl mb-8">Atelier</h1>

      <div className="flex gap-4 mb-8 border-b border-[#E6DED5]">
        <button onClick={() => setTab("products")} className={`pb-3 overline ${tab === "products" ? "border-b-2 border-[#4A1525] text-[#4A1525]" : ""}`} data-testid="tab-products">Products ({products.length})</button>
        <button onClick={() => setTab("orders")} className={`pb-3 overline ${tab === "orders" ? "border-b-2 border-[#4A1525] text-[#4A1525]" : ""}`} data-testid="tab-orders">Orders ({orders.length})</button>
      </div>

      {tab === "products" && (
        <div className="grid lg:grid-cols-3 gap-8">
          <form onSubmit={submit} className="border border-[#E6DED5] p-6 space-y-4 bg-white lg:sticky lg:top-32 h-fit" data-testid="product-form">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-2xl" data-testid="form-title">{editingId ? "Edit product" : "Add product"}</h3>
              {editingId && (
                <button type="button" onClick={cancelEdit} className="text-[#5C5C5C] hover:text-[#4A1525] flex items-center gap-1 text-xs overline" data-testid="cancel-edit">
                  <X size={14} weight="light" /> Cancel
                </button>
              )}
            </div>
            <input placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border border-[#E6DED5] px-3 py-2 text-sm" required data-testid="admin-name" />
            <input placeholder="Slug" value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} className="w-full border border-[#E6DED5] px-3 py-2 text-sm" required data-testid="admin-slug" />
            <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border border-[#E6DED5] px-3 py-2 text-sm" rows={3} required data-testid="admin-desc" />
            <input type="number" step="0.01" placeholder="Price" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="w-full border border-[#E6DED5] px-3 py-2 text-sm" required data-testid="admin-price" />
            <div>
              <div className="overline mb-1 flex justify-between">
                <span>Category</span>
                <span className="text-[10px] text-[#5C5C5C] normal-case tracking-normal">Type new or pick existing</span>
              </div>
              <input
                list="admin-category-list"
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. loungewear, hosiery"
                className="w-full border border-[#E6DED5] px-3 py-2 text-sm"
                required
                data-testid="admin-category"
              />
              <datalist id="admin-category-list">
                {existingCategories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <input placeholder="Image URL" value={form.images[0]} onChange={e => setForm({ ...form, images: [e.target.value, form.images[1] || ""] })} className="hidden" data-testid="admin-img1" />
            <input placeholder="Second image URL (optional)" value={form.images[1] || ""} onChange={e => setForm({ ...form, images: [form.images[0], e.target.value] })} className="hidden" data-testid="admin-img2" />
            <ImageUploader value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} max={2} />
            <input placeholder="Colors (comma-separated)" value={Array.isArray(form.colors) ? form.colors.join(", ") : form.colors} onChange={e => setForm({ ...form, colors: e.target.value })} className="w-full border border-[#E6DED5] px-3 py-2 text-sm" data-testid="admin-colors" />
            <input placeholder="Sizes (comma-separated)" value={Array.isArray(form.sizes) ? form.sizes.join(", ") : form.sizes} onChange={e => setForm({ ...form, sizes: e.target.value })} className="w-full border border-[#E6DED5] px-3 py-2 text-sm" data-testid="admin-sizes" />
            <input type="number" placeholder="Stock" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} className="w-full border border-[#E6DED5] px-3 py-2 text-sm" data-testid="admin-stock" />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} data-testid="admin-featured" />
              Feature on homepage
            </label>
            <button type="submit" className="w-full bg-[#4A1525] text-[#FAF8F5] py-3 overline flex items-center justify-center gap-2" data-testid="admin-submit">
              {editingId ? <><PencilSimple size={14} weight="light" /> Save changes</> : <><Plus size={14} weight="light" /> Add product</>}
            </button>
          </form>

          <div className="lg:col-span-2 space-y-3" data-testid="admin-products-list">
            {products.map(p => (
              <div key={p.id} className={`flex items-center gap-4 border p-4 bg-white transition-colors ${editingId === p.id ? "border-[#4A1525]" : "border-[#E6DED5]"}`} data-testid={`admin-product-${p.slug}`}>
                <img src={p.images[0]} alt={p.name} className="w-16 h-20 object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-serif text-lg truncate">{p.name}</div>
                  <div className="overline text-[#5C5C5C]">{p.category} · ₹{p.price} · {p.stock} in stock {p.featured && "· Featured"}</div>
                </div>
                <button onClick={() => startEdit(p)} className="p-2 text-[#4A1525] hover:bg-[#F0EBE1]" data-testid={`edit-${p.slug}`} aria-label="Edit"><PencilSimple size={16} weight="light" /></button>
                <button onClick={() => del(p.id)} className="p-2 text-[#7A2020] hover:bg-[#F0EBE1]" data-testid={`delete-${p.slug}`} aria-label="Delete"><Trash size={16} weight="light" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-3" data-testid="admin-orders-list">
          {orders.length === 0 ? <p className="text-[#5C5C5C]">No orders yet.</p> : orders.map(o => {
            const hasGift = (o.items || []).some(i => i.is_gift);
            const giftItem = (o.items || []).find(i => i.is_gift);
            return (
            <div key={o.id} className="border border-[#E6DED5] p-4 bg-white grid grid-cols-6 gap-4 items-center">
              <div>
                <div className="overline text-[#5C5C5C]">Order</div>
                <div className="text-sm font-mono">{o.id.slice(0, 8)}</div>
                {hasGift && (
                  <div className="mt-1.5 inline-block bg-[#4A1525] text-[#FAF8F5] overline text-[9px] px-2 py-0.5" data-testid={`gift-tag-${o.id.slice(0,8)}`} title={giftItem?.gift_note || "Gift"}>
                    ✦ Gift{giftItem?.gift_recipient_name ? ` — ${giftItem.gift_recipient_name}` : ""}
                  </div>
                )}
              </div>
              <div>
                <div className="overline text-[#5C5C5C]">Date</div>
                <div className="text-sm">{new Date(o.created_at).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="overline text-[#5C5C5C]">Amount</div>
                <div className="text-sm">₹{o.amount?.toFixed(0)}</div>
              </div>
              <div>
                <div className="overline text-[#5C5C5C]">Method</div>
                <div className="text-sm uppercase">{o.payment_method === "cod" ? "COD" : "Card"}</div>
              </div>
              <div className={`overline px-3 py-1 text-center ${o.payment_status === "paid" ? "bg-[#2D4A22] text-[#FAF8F5]" : o.payment_status === "cod_pending" ? "bg-[#4A1525] text-[#FAF8F5]" : "bg-[#F0EBE1]"}`} data-testid={`order-status-${o.id.slice(0,8)}`}>
                {o.payment_status === "cod_pending" ? "COD pending" : o.payment_status}
              </div>
              <div>
                {o.payment_method === "cod" && o.payment_status !== "paid" && (
                  <button onClick={() => markPaid(o.id)} className="overline text-xs border border-[#4A1525] text-[#4A1525] px-3 py-2 hover:bg-[#4A1525] hover:text-[#FAF8F5] transition-colors" data-testid={`mark-paid-${o.id.slice(0,8)}`}>
                    Mark paid
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
